import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DatePickerField } from '../components/DatePickerField';
import { format, subDays } from 'date-fns';
import { History, Calendar as CalendarIcon, Search, ChevronDown, ChevronRight } from 'lucide-react';

const ENTITY_TYPES = ['', 'supplier', 'customer', 'sales_agent', 'purchase', 'production', 'order', 'expense', 'pack_size'];

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('');
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityType) params.append('entity_type', entityType);
      params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      params.append('end_date', format(endDate, 'yyyy-MM-dd') + 'T23:59:59');
      params.append('limit', '200');
      
      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-700',
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[action] || 'bg-slate-100 text-slate-700'}>{action}</Badge>;
  };

  const formatTimestamp = (ts) => {
    return new Date(ts).toLocaleString('en-KE', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Audit Log</h1>
        <p className="text-slate-500 mt-1">Track all system changes</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Entity Type</Label>
              <Select value={entityType || "all"} onValueChange={(v) => setEntityType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {ENTITY_TYPES.filter(Boolean).map((type) => (
                    <SelectItem key={type} value={type}>{type.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">From</Label>
              <DatePickerField
                date={startDate}
                onDateChange={setStartDate}
                buttonClassName="w-40 justify-start text-left font-normal"
                displayFormat="MMM d"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">To</Label>
              <DatePickerField
                date={endDate}
                onDateChange={setEndDate}
                buttonClassName="w-40 justify-start text-left font-normal"
                displayFormat="MMM d"
              />
            </div>
            <Button onClick={fetchLogs} className="btn-primary gap-2" data-testid="search-audit-btn">
              <Search className="h-4 w-4" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <History className="h-5 w-5 text-slate-500" />
            <CardTitle className="text-lg font-heading">Activity Log</CardTitle>
            <Badge variant="outline">{logs.length} records</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4D3E]"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No audit logs found for the selected criteria
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Entity ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow
                      className="table-row cursor-pointer"
                      onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      data-testid={`audit-row-${log.id}`}
                    >
                      <TableCell>
                        {(log.changes || log.before_data || log.after_data) && (
                          expandedLog === log.id ? (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          )
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{formatTimestamp(log.timestamp)}</TableCell>
                      <TableCell className="font-medium">{log.user_name}</TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell className="capitalize">{log.entity_type.replace('_', ' ')}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{log.entity_id.slice(0, 8)}...</TableCell>
                    </TableRow>
                    {expandedLog === log.id && (log.changes || log.before_data || log.after_data) && (
                      <TableRow>
                        <TableCell colSpan={6} className="bg-slate-50 p-4">
                          {log.changes && Object.keys(log.changes).length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-slate-700">Changes:</p>
                              <div className="grid gap-2">
                                {Object.entries(log.changes).map(([key, value]) => (
                                  <div key={key} className="text-sm">
                                    <span className="font-medium text-slate-600">{key}:</span>{' '}
                                    <span className="text-red-600 line-through">{JSON.stringify(value.before)}</span>{' '}
                                    <span className="text-green-600">→ {JSON.stringify(value.after)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-slate-500">
                              <pre className="whitespace-pre-wrap overflow-auto max-h-40 text-xs">
                                {JSON.stringify(log.after_data || log.before_data, null, 2)}
                              </pre>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
